import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MapPin, CalendarCheck, Send, Building2, Home } from "lucide-react";
import { CALENDLY_URL } from "@/components/Navbar";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EASE = [0.16, 1, 0.3, 1];

const INFO = [
  { icon: Phone, label: "Téléphone", value: "+33 6 60 97 64 87", href: "tel:+33660976487", testid: "contact-info-phone" },
  { icon: Mail, label: "Email", value: "contact@syndicappli.fr", href: "mailto:contact@syndicappli.fr", testid: "contact-info-email" },
  { icon: MapPin, label: "Adresse", value: "6, rue Léonard De Vinci, CS 20119, 53001 Laval", testid: "contact-info-address" },
];

export const Contact = () => {
  const [form, setForm] = useState({ nom: "", email: "", telephone: "", profil: "", message: "" });
  const [sending, setSending] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message envoyé ! Nous revenons vers vous rapidement.");
      setForm({ nom: "", email: "", telephone: "", profil: "", message: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Envoi impossible pour le moment, réessayez.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section
      id="contact"
      className="relative overflow-hidden bg-[linear-gradient(115deg,#2BC97E_0%,#0BC1D6_55%,#00C4CC_100%)] py-24 lg:py-32"
      data-testid="contact-section"
    >
      <div className="pointer-events-none absolute -left-20 top-16 h-64 w-64 rounded-full bg-white/10 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-24 right-0 h-80 w-80 rounded-full bg-white/10 blur-3xl" />

      <div className="relative mx-auto grid max-w-7xl gap-14 px-6 lg:grid-cols-2 lg:gap-20 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.8, ease: EASE }}
          className="text-white"
        >
          <span className="inline-flex items-center gap-3 text-xs font-bold uppercase tracking-[0.25em] text-white/85">
            <span className="h-px w-10 bg-white/70" />
            Nous contacter
          </span>
          <h2 className="mt-6 text-4xl font-bold tracking-normal leading-[1.15] md:text-5xl lg:text-6xl">
            Parlons de votre{" "}
            <span className="font-extrabold">copropriété.</span>
          </h2>
          <p className="mt-5 max-w-md text-base leading-relaxed text-white/85">
            Une question sur Syndicappli ou sur nos offres à 360° ? Écrivez-nous
            ou réservez directement une démonstration de 30 minutes.
          </p>

          <div className="mt-10 space-y-6">
            {INFO.map(({ icon: Icon, label, value, href, testid }) => (
              <div key={label} className="flex items-start gap-4" data-testid={testid}>
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-white/15 text-white">
                  <Icon size={18} />
                </span>
                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.18em] text-white/60">
                    {label}
                  </p>
                  {href ? (
                    <a
                      href={href}
                      className="mt-0.5 block text-base font-semibold transition-opacity hover:opacity-80"
                    >
                      {value}
                    </a>
                  ) : (
                    <p className="mt-0.5 text-base font-semibold">{value}</p>
                  )}
                </div>
              </div>
            ))}
          </div>

          <a
            href={CALENDLY_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="contact-cta-demo-button"
            className="mt-10 inline-flex items-center gap-3 rounded-full bg-white px-8 py-4 text-sm font-bold text-ink shadow-[0_18px_40px_-15px_rgba(0,60,70,0.5)] transition-transform duration-300 hover:scale-105 active:scale-95"
          >
            <CalendarCheck size={17} className="text-electric" />
            Réserver une démo — 30 min
          </a>
        </motion.div>

        <motion.form
          onSubmit={submit}
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.8, delay: 0.15, ease: EASE }}
          className="h-fit rounded-[1.75rem] bg-white p-8 shadow-[0_45px_90px_-30px_rgba(0,55,70,0.55)] md:p-11"
          data-testid="contact-form"
        >
          <h3 className="text-center text-2xl font-semibold text-ink">
            Écrivez-nous
          </h3>
          <div className="mt-8 grid gap-6 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <span className="text-sm font-medium text-ink/70">Vous êtes *</span>
              <div className="mt-1.5 grid grid-cols-2 gap-3" role="radiogroup" aria-label="Vous êtes">
                {[
                  { value: "Syndic", Icon: Building2, testid: "contact-radio-syndic" },
                  { value: "Copropriétaire", Icon: Home, testid: "contact-radio-coproprietaire" },
                ].map(({ value, Icon, testid }) => (
                  <label
                    key={value}
                    className={`flex cursor-pointer items-center justify-center gap-2 rounded-full border px-4 py-3 text-sm font-semibold transition-colors duration-200 ${
                      form.profil === value
                        ? "border-electric bg-electric/10 text-electric"
                        : "border-black/10 bg-white text-ink/60 hover:border-electric/50"
                    }`}
                  >
                    <input
                      type="radio"
                      name="profil"
                      value={value}
                      required
                      checked={form.profil === value}
                      onChange={() => setForm((f) => ({ ...f, profil: value }))}
                      className="sr-only"
                      data-testid={testid}
                    />
                    <Icon size={16} />
                    {value}
                  </label>
                ))}
              </div>
            </div>
            <div>
              <label htmlFor="contact-nom" className="text-sm font-medium text-ink/70">
                Nom *
              </label>
              <input
                id="contact-nom"
                type="text"
                required
                minLength={2}
                maxLength={100}
                value={form.nom}
                onChange={set("nom")}
                placeholder="Votre nom"
                className="input-app mt-1.5"
                data-testid="contact-input-nom"
              />
            </div>
            <div>
              <label htmlFor="contact-email" className="text-sm font-medium text-ink/70">
                Email *
              </label>
              <input
                id="contact-email"
                type="email"
                required
                value={form.email}
                onChange={set("email")}
                placeholder="vous@exemple.fr"
                className="input-app mt-1.5"
                data-testid="contact-input-email"
              />
            </div>
          </div>
          <div className="mt-6">
            <label htmlFor="contact-telephone" className="text-sm font-medium text-ink/70">
              Téléphone
            </label>
            <input
              id="contact-telephone"
              type="tel"
              value={form.telephone}
              onChange={set("telephone")}
              placeholder="+33 6 00 00 00 00"
              className="input-app mt-1.5"
              data-testid="contact-input-telephone"
            />
          </div>
          <div className="mt-6">
            <label htmlFor="contact-message" className="text-sm font-medium text-ink/70">
              Message *
            </label>
            <textarea
              id="contact-message"
              required
              minLength={5}
              maxLength={3000}
              rows={5}
              value={form.message}
              onChange={set("message")}
              placeholder="Dites-nous tout..."
              className="input-app mt-1.5 resize-none"
              data-testid="contact-input-message"
            />
          </div>
          <button
            type="submit"
            disabled={sending}
            className="mt-8 flex w-full items-center justify-center gap-3 rounded-full bg-[linear-gradient(100deg,#00C6CC,#2FCB74)] px-8 py-4 text-sm font-bold text-white shadow-[0_18px_40px_-12px_rgba(0,190,200,0.6)] transition-all duration-300 hover:scale-[1.02] hover:opacity-90 active:scale-95 disabled:opacity-60"
            data-testid="contact-form-submit-button"
          >
            <Send size={16} />
            {sending ? "Envoi en cours..." : "Envoyer le message"}
          </button>
          <p className="mt-4 text-center text-xs text-ink/40">
            Nous ne demandons jamais de mot de passe par email.
          </p>
        </motion.form>
      </div>
    </section>
  );
};
